/* WhGoalAndWins (iter454) — Goal Engine + Wins Engine UI.
 *
 * Renders 3 patient-centric blocks:
 *   1. Primary Goal selector (one-time) — "Каква е твоята цел?"
 *   2. Dynamic weekly goal — "Тази седмица: цел според твоята основна цел + top driver"
 *   3. Wins panel — "Твоите победи за 30 дни"
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Calendar, Sparkles, Heart, TrendingDown, Sun, Zap, Flame, Moon,
  CheckCircle, Target, ChevronRight, Loader2, Pencil,
} from "lucide-react";
import { formatApiError } from "@/lib/formatApiError";

const GOAL_ICON = {
  Calendar, Sparkles, Heart, TrendingDown, Sun, Zap, Flame, Moon,
};

const WIN_ICON = {
  Calendar, Sparkles, Heart, TrendingDown, Sun, Zap, Flame, Moon, CheckCircle,
};

export default function WhGoalAndWins({ API, isDark }) {
  const [goal, setGoal] = useState(null);
  const [library, setLibrary] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [wins, setWins] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: lib }, { data: w }, { data: wn }] = await Promise.all([
        axios.get(`${API}/wh/v3/goal`),
        axios.get(`${API}/wh/v3/goals/library`),
        axios.get(`${API}/wh/v3/weekly-goal-dynamic`),
        axios.get(`${API}/wh/v3/wins?days=30`),
      ]);
      setGoal(g);
      setLibrary(lib.goals || []);
      setWeekly(w);
      setWins(wn);
      if (!g.has_goal) setShowPicker(true);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  // Refresh when quick-logs are saved
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("quick-log-saved", handler);
    return () => window.removeEventListener("quick-log-saved", handler);
  }, [load]);

  const chooseGoal = async (key) => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/wh/v3/goal`, { primary_goal: key });
      toast.success("Целта е запазена");
      setShowPicker(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Грешка при запазване на целта"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-pink-500 mx-auto my-4" />;

  return (
    <div className="space-y-3" data-testid="wh-goal-wins">
      {/* Primary goal pill */}
      {goal?.has_goal && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
          <CardContent className="p-4 sm:p-5 flex items-start gap-3" data-testid="wh-primary-goal-card">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-white flex-shrink-0">
              <Target className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                Твоята основна цел
              </p>
              <h3 className="wh-heading text-lg">{goal.label_bg}</h3>
              <p className={`text-xs mt-1 ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>
                {goal.expected_outcome_bg}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className={`text-[11px] inline-flex items-center gap-1 ${isDark ? "text-pink-400 hover:text-pink-300" : "text-pink-600 hover:text-pink-700"}`}
              data-testid="wh-edit-goal"
            >
              <Pencil className="w-3 h-3" /> Промени
            </button>
          </CardContent>
        </Card>
      )}

      {/* Dynamic weekly goal */}
      {weekly?.has_goal && (
        <Card className={isDark ? "bg-emerald-950/30 border-emerald-900" : "bg-emerald-50 border-emerald-200"}>
          <CardContent className="p-4 sm:p-5 flex items-start gap-3" data-testid="wh-weekly-dynamic-goal">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white flex-shrink-0">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                Тази седмица — цел спрямо твоята основна цел и водещ фактор
              </p>
              <p className={`text-sm font-bold mt-0.5 ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
                {weekly.weekly_target_bg}
              </p>
              <p className={`text-[11px] mt-1 ${isDark ? "text-emerald-300/70" : "text-emerald-700/80"}`}>
                Цел: <b>{weekly.target_value}</b>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wins */}
      {wins && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
          <CardContent className="p-4 sm:p-5" data-testid="wh-wins-card">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              Твоите победи за последните {wins.period_days || 30} дни
            </p>
            {(!wins.wins || wins.wins.length === 0) ? (
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {wins.empty_message_bg || "Все още нямаме достатъчно данни за победи."}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {wins.wins.map((w) => {
                  const Icon = WIN_ICON[w.icon] || CheckCircle;
                  const color = w.direction === "improving"
                    ? (isDark ? "text-emerald-300" : "text-emerald-700")
                    : w.direction === "needs_work"
                      ? (isDark ? "text-amber-300" : "text-amber-700")
                      : (isDark ? "text-slate-300" : "text-[#5C665D]");
                  return (
                    <li key={w.key} className="flex items-center gap-2 text-sm" data-testid={`wh-win-${w.key}`}>
                      <Icon className={`w-4 h-4 ${color}`} />
                      <span className={isDark ? "text-slate-100" : "text-[#2A342B]"}>{w.label_bg}</span>
                      <span className={`text-[11px] font-bold ${color}`}>{w.delta_bg}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Goal picker dialog */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700 max-h-[85vh] overflow-y-auto" data-testid="wh-goal-picker">
          <DialogHeader>
            <DialogTitle>Каква е твоята основна цел?</DialogTitle>
            <DialogDescription>
              Програмата ще се адаптира спрямо твоя избор — седмични цели, действия и победи.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {library.map((g) => {
              const Icon = GOAL_ICON[g.icon] || Target;
              const isActive = goal?.primary_goal === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => chooseGoal(g.key)}
                  disabled={saving}
                  className={`text-left p-3 rounded-2xl border-2 transition ${
                    isActive
                      ? "border-pink-500 bg-pink-50 dark:bg-pink-950/30"
                      : "border-slate-200 dark:border-slate-700 hover:border-pink-400"
                  }`}
                  data-testid={`wh-goal-option-${g.key}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-pink-500" />
                    <p className="font-bold text-sm">{g.label_bg}</p>
                  </div>
                  <p className="text-[11px] opacity-75 leading-tight">{g.expected_outcome_bg}</p>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
